/**
 * JobsPage — three-tab layout: Your Library / Discover / New Job.
 *
 * Your Library  — tenant's curated template grid (run-by-tenant OR published
 *                 tenant-authored). Favorites pinned, recency-sorted otherwise.
 *                 Has inline search bar.
 * Discover      — unrun Linqage system templates, split into Featured /
 *                 Recommended sections. Has inline search bar.
 * New Job       — inline DescribeJobSheet-style form (no search bar).
 *
 * Viewer guard: users without create_job see a plain nudge to /activity.
 *
 * See docs/product/jobs-and-activity.md §4.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { Tabs } from '@/components/Tabs/Tabs';
import { Icon } from '@/lib/icon';
import { t } from '@/lib/i18n';
import { usePermission } from '@/stores/auth';
import { useJobsPage, useFavoriteTemplate } from '../hooks';
import { filterByQuery } from '../searchUtils';
import type { JobsPageSuggestedItem, JobsPageYoursItem, JobStatus } from '../schemas';
import { DiscoverTab } from '../components/DiscoverTab';
import { JobsSearchInput } from '../components/JobsSearchInput';
import { NewJobTab } from '../components/NewJobTab';
import { RunJobSheet } from '../components/RunJobSheet';
import styles from './JobsPage.module.css';

type Tab = 'library' | 'discover' | 'new-job';

const STATUS_PILL_VARIANT: Record<JobStatus, 'info' | 'success' | 'warning' | 'danger'> = {
  running: 'info',
  awaiting_input: 'warning',
  completed: 'success',
  failed: 'danger',
  cancelled: 'info',
};

const STATUS_LABEL: Record<JobStatus, string> = {
  running: t('In progress'),
  awaiting_input: t('Needs input'),
  completed: t('Done'),
  failed: t('Failed'),
  cancelled: t('Cancelled'),
};

export function JobsPage() {
  const { data, isLoading, isError } = useJobsPage();
  const canCreateJob = usePermission('create_job');
  const [activeTab, setActiveTab] = useState<Tab>('library');
  const [searchQuery, setSearchQuery] = useState('');
  const [runTemplateId, setRunTemplateId] = useState<string | null>(null);

  function switchTab(tab: Tab) {
    setActiveTab(tab);
    setSearchQuery('');
  }

  const onRun = (templateId: string) => setRunTemplateId(templateId);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title={t('Jobs')} subtitle={t('Everything your team knows how to do.')} />
        <p className={styles.muted}>{t('Loading…')}</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.page}>
        <PageHeader title={t('Jobs')} subtitle={t('Everything your team knows how to do.')} />
        <p className={styles.muted}>{t('Could not load the Jobs page.')}</p>
      </div>
    );
  }

  if (!canCreateJob) {
    return (
      <div className={styles.page}>
        <PageHeader title={t('Jobs')} subtitle={t('Everything your team knows how to do.')} />
        <p className={styles.muted}>
          {t("Your team's jobs will appear here —")}{' '}
          <Link to="/activity">{t("check Activity to see what's in progress.")}</Link>
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title={t('Jobs')} subtitle={t('Everything your team knows how to do.')} />

      <Tabs.Root value={activeTab} onValueChange={(v) => switchTab(v as Tab)}>
        <Tabs.List aria-label={t('Jobs navigation')}>
          <Tabs.Trigger value="library">{t('Your Library')}</Tabs.Trigger>
          <Tabs.Trigger value="discover">{t('Discover')}</Tabs.Trigger>
          <Tabs.Trigger value="new-job">{t('New Job')}</Tabs.Trigger>
        </Tabs.List>

        {activeTab === 'library' ? (
          <JobsSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('Filter your library…')}
          />
        ) : null}

        {/*
          Only render the active panel — Radix mounts all Content nodes via
          Presence's render-prop path, so inactive panels remain in the DOM
          and create phantom flex-gap above later panels. Same pattern as
          LibraryHubPage.
        */}
        {activeTab === 'library' && (
          <Tabs.Content value="library">
            <LibraryTabContent
              yours={data.yours}
              suggested={data.suggested.slice(0, 3)}
              searchQuery={searchQuery}
              onRun={onRun}
              onBrowseSuggestions={() => switchTab('discover')}
              onDescribeJob={() => switchTab('new-job')}
            />
          </Tabs.Content>
        )}

        {activeTab === 'discover' && (
          <Tabs.Content value="discover">
            <DiscoverTab canCreateJob={canCreateJob} onRun={onRun} />
          </Tabs.Content>
        )}

        {activeTab === 'new-job' && (
          <Tabs.Content value="new-job">
            <NewJobTab />
          </Tabs.Content>
        )}
      </Tabs.Root>

      {runTemplateId !== null ? (
        <RunJobSheet
          open
          onOpenChange={(o) => {
            if (!o) setRunTemplateId(null);
          }}
          templateId={runTemplateId}
        />
      ) : null}
    </div>
  );
}

function LibraryTabContent({
  yours,
  suggested,
  searchQuery,
  onRun,
  onBrowseSuggestions,
  onDescribeJob,
}: {
  yours: JobsPageYoursItem[];
  suggested: JobsPageSuggestedItem[];
  searchQuery: string;
  onRun: (templateId: string) => void;
  onBrowseSuggestions: () => void;
  onDescribeJob: () => void;
}) {
  const filtered = useMemo(() => filterByQuery(yours, searchQuery), [yours, searchQuery]);
  const isSearchActive = searchQuery.trim().length > 0;

  if (yours.length === 0 && !isSearchActive) {
    return (
      <div className={styles.coach}>
        <p className={styles.coachEyebrow}>{t('Nothing here yet')}</p>
        <h2 className={styles.coachHeading}>{t("Your team's ready. What's on the list?")}</h2>
        <p className={styles.coachLede}>
          {t(
            'Run a job once and it shows up here — easy to run again any time. Start with one of these:',
          )}
        </p>
        {suggested.length > 0 ? (
          <div className={styles.coachGrid}>
            {suggested.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.coachTile}
                onClick={onBrowseSuggestions}
              >
                <span className={styles.tileIcon} aria-hidden="true">
                  <Icon iconRef={item.icon} size={18} />
                </span>
                <span className={styles.tileBody}>
                  <span className={styles.tileTitle}>{item.title}</span>
                  {item.description ? (
                    <span className={styles.tileDesc}>{item.description}</span>
                  ) : null}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        <div className={styles.coachFooter}>
          {suggested.length > 0 ? (
            <>
              {t("Don't see what you need?")}{' '}
              <button type="button" className={styles.coachFooterLink} onClick={onDescribeJob}>
                {t('Describe a job in plain English →')}
              </button>
            </>
          ) : (
            <div className={styles.emptyLibraryActions}>
              <Button variant="secondary" size="sm" onClick={onBrowseSuggestions}>
                {t('Browse suggestions →')}
              </Button>
              <Button variant="secondary" size="sm" onClick={onDescribeJob}>
                {t('Describe a Job →')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isSearchActive && filtered.length === 0) {
    return (
      <div className={styles.noResults}>
        <p className={styles.noResultsCopy}>
          {t('Nothing matches — try describing what you need.')}
        </p>
        <button type="button" className={styles.inlineLink} onClick={onDescribeJob}>
          {t('Describe a Job →')}
        </button>
      </div>
    );
  }

  return (
    <section aria-label={t("Your team's jobs")}>
      <div className={styles.grid}>
        {filtered.map((item) => (
          <YoursCard key={item.id} item={item} onRun={onRun} />
        ))}
      </div>
    </section>
  );
}

function YoursCard({
  item,
  onRun,
}: {
  item: JobsPageYoursItem;
  onRun: (templateId: string) => void;
}) {
  const favoriteMut = useFavoriteTemplate();
  const hasRuns = item.run_count > 0;
  const verb = hasRuns ? t('Run again') : t('Run');

  return (
    <Card padding="md" tone={item.favorited ? 'highlight' : 'default'}>
      <div className={styles.cardHead}>
        <Icon iconRef={item.icon} size={24} />
        <Link
          to={`/library/job-profiles/${encodeURIComponent(item.id)}`}
          className={styles.cardTitleBlock}
        >
          <h3 className={styles.cardTitle}>{item.title}</h3>
          {item.description ? <p className={styles.cardDesc}>{item.description}</p> : null}
        </Link>
        <button
          type="button"
          className={styles.favoriteBtn}
          aria-label={item.favorited ? t('Unfavorite') : t('Favorite')}
          aria-pressed={item.favorited}
          onClick={() => favoriteMut.mutate({ templateId: item.id, favorite: !item.favorited })}
        >
          {item.favorited ? '★' : '☆'}
        </button>
      </div>

      <div className={styles.cardMeta}>
        {item.last_status ? (
          <StatusPill
            variant={STATUS_PILL_VARIANT[item.last_status]}
            dot={item.last_status === 'running'}
          >
            {STATUS_LABEL[item.last_status]}
          </StatusPill>
        ) : (
          <span className={styles.neverRunBadge}>{t('Never run')}</span>
        )}
        {hasRuns ? (
          <span className={styles.runCount}>
            {item.run_count} {item.run_count === 1 ? t('run') : t('runs')}
          </span>
        ) : null}
      </div>

      <div className={styles.cardActions}>
        <Button variant="primary" size="sm" onClick={() => onRun(item.id)}>
          {verb}
        </Button>
        <Link
          to={`/activity?filter=template:${encodeURIComponent(item.id)}`}
          className={styles.subtleLink}
        >
          {t('View runs →')}
        </Link>
      </div>
    </Card>
  );
}
